import { renderGenerativeUI } from "@assistant-ui/react-generative-ui";
import { ExternalLinkIcon, FileTextIcon, LayoutGridIcon, TableIcon } from "lucide-react";
import { motion } from "motion/react";
import type { ComponentType } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { CaptainAvatar } from "./CaptainAvatar";
import type { DisplayPayload } from "../utils/displayTool";
import { type CellValue, type DataTableColumn, DataTable } from "./gen-ui/DataTable";
import { InfoCard } from "./gen-ui/InfoCard";
import captainLibrary from "./gen-ui/library";
import { SpecSheet, type SpecRow } from "./gen-ui/SpecSheet";
import "./gen-ui/gen-ui.css";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/v2/dialog";
import {
  Message,
  MessageAvatar,
  MessageContent,
} from "@/components/ui/v2/message";

function formatFetchedAt(iso: string | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return null;
  }
}

type NavigationAction = {
  clientId?: string;
  integration?: string;
  module?: string;
  label?: string;
};

function NavigationButton({ action, onNavigate }: { action: NavigationAction; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleClick = () => {
    const parts = pathname.split("/");
    const clientsIdx = parts.indexOf("clients");
    const urlClientId = clientsIdx !== -1 ? parts[clientsIdx + 1] : undefined;
    const effectiveClientId = urlClientId || action.clientId;
    if (!effectiveClientId) return;

    onNavigate?.();
    navigate(`/clients/${effectiveClientId}/integrations/${action.integration}/${action.module}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-muted px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-muted"
    >
      {action.label ?? t("captain.actions.open")}
      <ExternalLinkIcon className="size-3" />
    </button>
  );
}

function PreviewCard({
  icon: Icon,
  title,
  subtitle,
  fetchedAt,
  children,
  action,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  fetchedAt: string | null;
  children: React.ReactNode;
  action?: NavigationAction;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-3 rounded-xl border border-border px-4 py-3 text-left transition-colors hover:bg-muted"
      >
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
          <Icon className="size-4" />
        </div>
        <div className="flex min-w-0 flex-col">
          <span className="text-sm font-medium text-foreground">{title}</span>
          <span className="text-xs text-muted-foreground">
            {subtitle}
            {fetchedAt ? ` · ${t("captain.display.lastUpdatedAt", { time: fetchedAt })}` : ""} — {t("captain.display.clickToView")}
          </span>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex h-[80vh] w-[80vw] !max-w-none flex-col">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {fetchedAt && (
              <p className="text-xs text-muted-foreground">Last updated at {fetchedAt}</p>
            )}
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pr-3">
            {action?.integration && (
              <div className="mb-2 flex justify-end">
                <NavigationButton action={action} onNavigate={() => setOpen(false)} />
              </div>
            )}
            {children}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const CARD_INLINE_THRESHOLD = 3;

export function DisplayToolResult({
  payload,
  showAvatar = true,
}: {
  payload: DisplayPayload;
  showAvatar?: boolean;
}) {
  const { t } = useTranslation();
  const title = (payload.title as string) ?? t("captain.toolResult", "Results");
  const fetchedAt = formatFetchedAt(payload.fetchedAt as string | undefined);
  const action = payload.action as NavigationAction | undefined;

  const isTable = payload.$type === "Table";
  const isSpecSheet = payload.$type === "SpecSheet";
  const isInfoCard = payload.$type === "InfoCard";
  const cardCount = isInfoCard ? ((payload.cards as unknown[]) ?? []).length : 0;
  const isLargeCardSet = isInfoCard && cardCount > CARD_INLINE_THRESHOLD;

  let content: React.ReactNode;

  if (isTable) {
    const rows = (payload.rows as unknown[] | undefined) ?? [];
    content = (
      <PreviewCard
        icon={TableIcon}
        title={title}
        subtitle={t("captain.display.results", { count: rows.length })}
        fetchedAt={fetchedAt}
        action={action}
      >
        <DataTable columns={payload.columns as DataTableColumn[]} rows={payload.rows as CellValue[][]} />
      </PreviewCard>
    );
  } else if (isSpecSheet) {
    const rows = (payload.rows as SpecRow[] | undefined) ?? [];
    content = (
      <PreviewCard
        icon={FileTextIcon}
        title={title}
        subtitle={`${rows.length} ${rows.length === 1 ? "field" : "fields"}`}
        fetchedAt={fetchedAt}
      >
        <SpecSheet rows={rows} visibleCount={rows.length} className="border-0 p-0" />
      </PreviewCard>
    );
  } else if (isLargeCardSet) {
    const cards = (payload.cards as { title: string; description?: string; status?: string; tags?: string[] }[]) ?? [];
    content = (
      <PreviewCard
        icon={LayoutGridIcon}
        title={title}
        subtitle={`${cards.length} ${cards.length === 1 ? "item" : "items"}`}
        fetchedAt={fetchedAt}
      >
        <InfoCard cards={cards} />
      </PreviewCard>
    );
  } else {
    content = renderGenerativeUI(payload, captainLibrary);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
    >
      <Message align="start">
        {showAvatar && (
          <MessageAvatar>
            <CaptainAvatar />
          </MessageAvatar>
        )}
        <MessageContent>
          <div className="max-w-[90%]">{content}</div>
        </MessageContent>
      </Message>
    </motion.div>
  );
}
